import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Wallet, CheckCircle2, X, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Bright-orange tile that surfaces every pending Interac e-Transfer waiting
 * on finance verification. Hides itself when there's nothing pending so the
 * Payments page stays clean on a quiet day.
 *
 * One-click "Confirm" flips status → completed and refreshes parent data
 * via the optional onChange callback.
 */
export const PendingEtransfersWidget = ({ onChange }) => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/payments/pending-etransfers`,
        { withCredentials: true },
      );
      setItems(res.data?.items || []);
    } catch {
      // silent — widget hides itself when fetch fails (e.g. non-admin viewer)
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirm = async (paymentId) => {
    if (!window.confirm('Confirm funds have cleared in the bank account? This will mark the payment as completed and update the booking balance.')) return;
    setConfirmingId(paymentId);
    try {
      await axios.post(
        `${API_URL}/api/admin/payments/${paymentId}/confirm-etransfer`,
        {},
        { withCredentials: true },
      );
      toast.success('e-Transfer confirmed — booking balance updated');
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not confirm');
    } finally {
      setConfirmingId(null);
    }
  };

  const reject = async (paymentId) => {
    const reason = window.prompt('Reason for rejecting this e-Transfer? (optional)') || '';
    setConfirmingId(paymentId);
    try {
      await axios.post(
        `${API_URL}/api/admin/payments/${paymentId}/reject-etransfer`,
        { reason },
        { withCredentials: true },
      );
      toast.success('e-Transfer marked as rejected');
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reject');
    } finally {
      setConfirmingId(null);
    }
  };

  // Hide entirely on a clean day
  if (!loaded || items.length === 0) return null;

  const total = items.reduce((sum, p) => sum + Number(p.amount || 0), 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border-2 border-[#F59E0B]/60 p-5 mb-6"
      data-testid="pending-etransfers-widget"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center flex-shrink-0">
          <Wallet className="w-5 h-5 text-[#F59E0B]" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-[#1E1E24]">Pending e-Transfers</h3>
            <span className="text-[10px] bg-[#F59E0B]/15 text-[#F59E0B] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
              Action required
            </span>
          </div>
          <p className="text-xs text-[#6B6B7A] mt-0.5">
            {items.length} transfer{items.length === 1 ? '' : 's'} totaling{' '}
            <strong className="text-[#1E1E24]">${total.toFixed(2)}</strong> waiting on bank confirmation.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-xs text-[#6B6B7A] hover:text-[#1E1E24] flex items-center gap-1"
          data-testid="pending-etransfers-refresh"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="divide-y divide-[#F0EDE6] rounded-xl border border-[#F0EDE6] overflow-hidden">
        {items.map((p) => (
          <div
            key={p.payment_id}
            className="flex items-center justify-between px-3 py-2.5 hover:bg-[#FFFDF8] transition"
            data-testid={`pending-etransfer-row-${p.payment_id}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <ChevronRight className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#1E1E24] truncate">
                  ${Number(p.amount).toFixed(2)} from {p.customer_name || '—'}
                  <span className="text-[#6B6B7A] font-normal"> · {p.child_name}'s party · {p.party_date}</span>
                </p>
                <p className="text-[11px] text-[#6B6B7A] truncate">
                  Logged {new Date(p.created_at).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  {p.collected_by_name && ` by ${p.collected_by_name}`}
                  {p.notes && ` · ${p.notes.slice(0, 60)}${p.notes.length > 60 ? '…' : ''}`}
                </p>
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <Button
                size="sm"
                onClick={() => confirm(p.payment_id)}
                disabled={confirmingId === p.payment_id}
                className="h-7 bg-[#06D6A0] hover:bg-[#05B88A] text-white text-xs rounded-full px-3"
                data-testid={`pending-etransfer-confirm-${p.payment_id}`}
              >
                <CheckCircle2 className="w-3 h-3 mr-1" /> Confirm
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => reject(p.payment_id)}
                disabled={confirmingId === p.payment_id}
                className="h-7 w-7 p-0 text-[#E5484D] hover:bg-[#E5484D]/10"
                title="Reject — funds did not arrive"
                data-testid={`pending-etransfer-reject-${p.payment_id}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default PendingEtransfersWidget;
